import type { SearchHit } from '@up2d8/contracts';
import { formatDate } from './format';

/**
 * Wat een zoekresultaat toont en waar het heen gaat (Stap 34d), los van het scherm zodat het te
 * toetsen is.
 *
 * Testrapport 28 okt.: de datum van een bericht stond er rauw ("2026-10-14"), en een collega
 * opende alleen de lijst, zodat je hem daar opnieuw moest zoeken.
 */

/** Een dag zoals de server hem bij een bericht meegeeft: "2026-10-14". */
const DAY = /^\d{4}-\d{2}-\d{2}$/;

/**
 * De tweede regel van een resultaat. Bij een bericht is dat de publicatiedag; die komt als
 * "JJJJ-MM-DD" en wordt in de taal van de lezer opgemaakt ("14 okt. 2026"). In UTC, want het is
 * een dag en geen tijdstip: in een zone achter UTC zou middernacht anders de dag ervoor worden.
 */
export function searchHitSubtitle(hit: Pick<SearchHit, 'kind' | 'subtitle'>): string | null {
  if (!hit.subtitle) return null;
  if (hit.kind === 'post' && DAY.test(hit.subtitle)) {
    return formatDate(`${hit.subtitle}T00:00:00Z`, {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      timeZone: 'UTC',
    });
  }
  return hit.subtitle;
}

export type SearchTarget =
  | { to: '/admin/posts' }
  | { to: '/app/posts/$postId'; params: { postId: string } }
  | { to: '/admin/people'; search: { persoon: string } }
  | { to: '/app/collegas'; search: { persoon: string; zoek: string } }
  | { to: '/admin/documenten' }
  | { to: '/app/documenten' };

/**
 * Waar een tik op een resultaat heen gaat. De bestemming hangt aan de **soort** en aan de zone:
 * een beheerder die een bericht aantikt, wil het beheerscherm; een medewerker wil het bericht
 * zoals hij het krijgt.
 *
 * Een collega opent die persoon: in het beheer het paneel van de medewerker, in de app de
 * profielkaart op het bureauscherm. Op de telefoon bestaat geen profielkaart; daar staat de lijst
 * gefilterd op zijn naam, met de knop om een gesprek te beginnen ernaast.
 */
export function searchHitTarget(
  hit: Pick<SearchHit, 'kind' | 'id' | 'title'>,
  zone: 'app' | 'admin',
): SearchTarget {
  if (hit.kind === 'post') {
    return zone === 'admin'
      ? { to: '/admin/posts' }
      : { to: '/app/posts/$postId', params: { postId: hit.id } };
  }
  if (hit.kind === 'colleague') {
    return zone === 'admin'
      ? { to: '/admin/people', search: { persoon: hit.id } }
      : { to: '/app/collegas', search: { persoon: hit.id, zoek: hit.title } };
  }
  return zone === 'admin' ? { to: '/admin/documenten' } : { to: '/app/documenten' };
}
